import { FC, Fragment, ReactNode, useMemo } from "react";
import { useTranslation } from "@/i18n/client";
import { Box, Stack, Typography } from "@mui/material";

import color from "@/styles/color";

import { copyToClipboard } from "@/libs/utils/function";
import { ENV_CONFIG } from "@/libs";
import IconLoader from "../IconLoader";

type SocialIcon = {
  key: string;
  icon: ReactNode;
};

type ShareSocialProps = {
  link: string;
  showText?: boolean;
  setStateCopyLink?: () => void;
};

const ShareSocial: FC<ShareSocialProps> = ({ link, showText = true, setStateCopyLink }) => {
  const { t } = useTranslation();

  const url = useMemo(() => {
    return `${ENV_CONFIG.MAIN_URL}${link}`;
  }, [link]);

  const encodedUrl = useMemo(() => {
    return encodeURIComponent(url);
  }, [url]);

  const facebookShareUrl = useMemo(() => {
    return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  }, [encodedUrl]);

  const lineShareUrl = useMemo(() => {
    return `https://social-plugins.line.me/lineit/share?url=${encodedUrl}`;
  }, [encodedUrl]);

  const xShareUrl = useMemo(() => {
    return `https://x.com/intent/post?url=${encodedUrl}`;
  }, [encodedUrl]);

  const openSocialShareDialog = (url: string, width = 626, height = 436) => {
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const features = `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes`;

    window.open(url, "_blank", features);
  };

  const socialList: SocialIcon[] = [
    {
      key: "link",
      icon: (
        <Box
          onClick={() => {
            copyToClipboard(url);
            if (setStateCopyLink) setStateCopyLink();
          }}
          sx={{
            width: "32px",
            height: "32px",
            borderRadius: "100%",
            background: color.redGradient,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconLoader iconName="LinkIcon" sx={{ fontSize: "20px", color: color.white }} />
        </Box>
      ),
    },
    {
      key: "line",
      icon: (
        <Box
          onClick={() => openSocialShareDialog(lineShareUrl)}
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            aspectRatio: "1/1",
          }}
        >
          <IconLoader iconName="LineCircleIcon" sx={{ fontSize: "32px" }} />
        </Box>
      ),
    },
    {
      key: "facebook",
      icon: (
        <Box
          onClick={() => openSocialShareDialog(facebookShareUrl)}
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            aspectRatio: "1/1",
          }}
        >
          <IconLoader iconName="FacebookCircleIcon" sx={{ fontSize: "32px" }} />
        </Box>
      ),
    },
    {
      key: "x",
      icon: (
        <Box
          onClick={() => openSocialShareDialog(xShareUrl)}
          sx={{
            position: "relative",
            width: "100%",
            overflow: "hidden",
            aspectRatio: "1/1",
          }}
        >
          <IconLoader iconName="XCircleIcon" sx={{ fontSize: "32px" }} />
        </Box>
      ),
    },
  ];

  return (
    <Stack
      alignItems="center"
      flexDirection="row"
      gap={2}
      sx={{
        justifyContent: { xs: "center", sm: "" },
      }}
    >
      {showText && (
        <Typography
          sx={{
            color: color.black,
            fontSize: "1rem",
            fontWeight: 500,
            lineHeight: "27px",
          }}
        >
          {t(`common.share`)}
        </Typography>
      )}
      {socialList.map((social) => {
        return (
          <Fragment key={`social-share-${social.key}`}>
            <Box sx={{ cursor: "pointer" }}>{social.icon}</Box>
          </Fragment>
        );
      })}
    </Stack>
  );
};

export default ShareSocial;
